import { Component, Input, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs/Rx';

import { HtmlService } from './../../../../../../services/html.service';
import * as fromRoot from './../../../../../../store/reducers';
import { ResponseService } from '../../../../../../services/response.service';
import { TestContentService } from '../../../../../../services/test-content.service';
import { ScoringPageService } from '../../../../../../services/scoring-page.service';
import { TestNavigationService } from '../../../../../../services/test-navigation.service';
import { PreviewService } from '../../../../../../services/preview.service';
import { type } from 'os';
import { ComponentTypeEnum } from 'app/dtos/enums';

interface SectionScore {
    TestPartIndex: number;
    Score: number;
}

interface SectionQuestionCount {
    TestPartIndex: number;
    NumQuestions: number;
}

interface SectionScoresDto {
    SectionScores: SectionScore[];
}
interface SectionQuestionsDto {
    SectionQuestions: SectionQuestionCount[];
}

@Component({
    selector: 'ipp-scoring-page',
    template: require('./ipp-scoring-page.component.html'),
    changeDetection: ChangeDetectionStrategy.Default
})
export class IppScoringPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() title: string;
    @Input() htmlResourceId: number;

    testlets = [];
    questionsDto: SectionQuestionsDto = { SectionQuestions:[] };
    scoresDto: SectionScoresDto = { SectionScores:[] };
    totalQuestions: number = 0;
    totalScore: number = 0;

    totalReadingQuestions: number = 0;
    totalListeningQuestions: number = 0;
    totalReadingScore: number = 0;
    totalListeningScore: number = 0;


    disclaimerText: string = "";
    scoresReady: boolean = true;
    isLastScoresPage: boolean = false;

    private assessmentTestSubscription: Subscription;
    private assessmentTest: PgRendererDto.AssessmentTest;
    private testNavigationSubscription: Subscription;
    private testNavigation:  PgRendererDto.TestNavigation;

    constructor(
        private store: Store<fromRoot.State>,
        private responseService: ResponseService,
        private testContentService: TestContentService,
        private scoringPageService: ScoringPageService,
        private previewService: PreviewService,
        private testNavigationService: TestNavigationService,
        private htmlService: HtmlService,
        private route: ActivatedRoute
    ) {
        this.testNavigationSubscription = store.select(fromRoot.getTestNavigation).subscribe(testNavigation => {
            this.testNavigation = testNavigation;
            this.questionsDto = { SectionQuestions:[] };
            this.scoresDto = { SectionScores:[] };
            this.scoringPageUpdate(this.testNavigation, this.assessmentTest);
        });

        this.assessmentTestSubscription = store.select(fromRoot.getAssessmentTest).subscribe(assessmentTest => {
            this.assessmentTest = assessmentTest;
            this.questionsDto = { SectionQuestions:[] };
            this.scoresDto = { SectionScores:[] };
            this.scoringPageUpdate(this.testNavigation, this.assessmentTest);
        });
    }

    ngOnInit() {
        this.getDisclaimerText();
    }

    ngAfterViewInit() {
        
    }

    ngOnDestroy() {
        if (this.testNavigationSubscription) {
            this.testNavigationSubscription.unsubscribe();
        }
        if (this.assessmentTestSubscription) {
            this.assessmentTestSubscription.unsubscribe();
        }
    }

    scoringPageUpdate(testNavigation: PgRendererDto.TestNavigation, assessmentTest: PgRendererDto.AssessmentTest) {
        if (testNavigation && assessmentTest) {
            this.scoresReady = false;
            this.isLastScoresPage = this.testNavigation.testPartIndex === this.assessmentTest.TestParts.length-2;

            let testPartIndex = testNavigation.testPartIndex;
            this.testlets = this.testNavigationService.getTestletsByTestPart(assessmentTest.TestParts[testPartIndex], testPartIndex);
            
            // if this is the last scoring page, fetch the scores for the entire test (split into test parts)
            if (this.isLastScoresPage === true) {
                for(var i = 1; i < this.assessmentTest.TestParts.length-2; i++) {
                    // the first test part is the landing page, the last two test parts are the scoring/feedback pages and end of test page
                    let sectionTestlets = this.testNavigationService.getTestletsByTestPart(this.assessmentTest.TestParts[i], i);
                    this.getSectionScores(sectionTestlets, i);
                }
            } else {
                this.getSectionScores(this.testlets, this.testNavigation.testPartIndex);
            }
        }
    }

    clearCounters()
    {
        this.totalListeningQuestions = 0;
        this.totalReadingQuestions = 0;

        this.totalListeningScore = 0;
        this.totalReadingScore = 0;
    }

    getSectionScores(sectionTestlets, i) {

        this.clearCounters();

        let testletIds = [];
        let interactions = [];
        let testletDefs = [];
        let numListeningQuestions : number[] = [];
        let numReadingQuestions : number[] = [];

        sectionTestlets.forEach(t => {
            testletIds.push(t.testletId)
        });

        this.testContentService.getTestlets(testletIds, this.assessmentTest.Identifier).subscribe(
            testletDefs => {

                // get all interactions belonging to the testlets in this test part
                testletDefs.forEach(t => {
                    let testletInteractions = this.testContentService.getReferencedInteractionsByTestlet(t);
                    
                    if (testletInteractions.length >= 1) {
                        if (interactions.length >= 1) {
                            interactions = interactions.concat(testletInteractions);
                        } else interactions = testletInteractions;
                    }
                });

                let numQuestions = 0;

                
                interactions.forEach(i => {
                    if (i.InteractionTypeName != "Speaking") {
                        numQuestions++;
                    }
                })
                interactions.forEach(i => {
                    if (i.ComponentTypeId == ComponentTypeEnum.Listening) {
                        numListeningQuestions.push(i.InteractionId);
                    }
                })
                interactions.forEach(i => {
                    if (i.ComponentTypeId == ComponentTypeEnum.Reading) {
                        numReadingQuestions.push(i.InteractionId);
                    }
                })
                
            
                let x : SectionQuestionCount = {
                    NumQuestions: numQuestions,
                    TestPartIndex: i
                }

                this.questionsDto.SectionQuestions.push(x);
                
                this.totalListeningQuestions += numListeningQuestions.length;
                this.totalReadingQuestions += numReadingQuestions.length;

                // fetch all the responses associated with the interactions in this test part
                let responses = this.responseService.responses.filter(x => interactions.findIndex(i => i.InteractionId == x.InteractionId) >= 0);
                
                // fetch scores
                let totalScore = 0;

                this.testContentService.getPreviewResponseScores(responses).subscribe(responseScores => {
                    responseScores.ResponseScores.forEach(s => {
                        totalScore += s.Score;

                        if(numListeningQuestions.includes(s.InteractionId))
                        {
                            this.totalListeningScore += s.Score; 
                        }
                        if(numReadingQuestions.includes(s.InteractionId))
                        {
                            this.totalReadingScore += s.Score;
                        }
                    })
                    
                    let y : SectionScore = {
                        Score: totalScore,
                        TestPartIndex: i
                    }
                    this.scoresDto.SectionScores.push(y);

                    // on last scores page, if all test part scores have been fetched, sort by TestPartIndex and set the summary values, then set scoresReady to true
                    // the -3 is to exclude the test parts containing the landing page, last scoring page, and end of test testlets
                    if (this.isLastScoresPage === true && this.scoresDto.SectionScores.length === this.assessmentTest.TestParts.length-3) {

                        this.questionsDto.SectionQuestions.sort((a: SectionQuestionCount, b: SectionQuestionCount) => a.TestPartIndex - b.TestPartIndex);
                        this.scoresDto.SectionScores.sort((a: SectionScore, b: SectionScore) => a.TestPartIndex - b.TestPartIndex);

                        let questions = this.questionsDto.SectionQuestions.map(i => i.NumQuestions);
                        this.totalQuestions = questions.reduce((x, y) => { return x + y }, 0);
                        
                        let scores = this.scoresDto.SectionScores.map(i => i.Score);
                        this.totalScore = scores.reduce((x, y) => { return x + y }, 0);
                        
                        this.scoresReady = true;
                    } else {
                        // if this is NOT the last scoring page, then set scoresReady to true
                        this.scoresReady = !(this.isLastScoresPage === true);
                    }
                });
            }
        );
    }

    getDisclaimerText() {
        this.htmlService.getHtmlResource(this.htmlResourceId).subscribe(html => {
            this.disclaimerText = html;
        });
    }
}





// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/components/pg-renderer/item-body/item-body-panel/ipp/ipp-scoring-page/ipp-scoring-page.component.ts